import { ComponentEventBus, inject, needs, prop } from "fw";
import { MultiselectField } from "./multiselect-field";
import { MultiselectPill } from "./multiselect-pill";
import { difference } from "lodash-es";

// NOTE: this is legacy component - should be deleted with NewContactPageUI feature flag
@inject
@needs(MultiselectField, MultiselectPill)
export class MultiselectSelectorOld {
  @prop("Search options...") public searchText!: string;
  @prop("Create new option") public createNewText!: string;
  @prop("No options available") public noOptionsText!: string;
  @prop("All options used") public noAvailableOptionsText!: string;
  @prop("No options found") public noFilteredOptionsText!: string;

  @prop(undefined) public value!: string[];
  @prop(null) public options!: string[];
  @prop(false) public showAdd!: boolean;
  @prop(false) public showRemove!: boolean;
  @prop(() => null) private validate!: (
    options: string[]
  ) => Promise<string | null>;

  public displayOptions: boolean = false;
  public filter: string = "";
  public filteredOptions: string[] = [];
  private addedOptions: string[] = [];
  public validation: string = null;

  constructor(private ceb: ComponentEventBus) {}

  public attached() {
    if (this.value === null) {
      this.ceb.dispatch("update:value", []);
    }

    this.filterOptions();
  }

  public addOption(option: string) {
    try {
      if (this.value && this.containsOption(this.value, option)) {
        this.closeOptions();
        return;
      }

      this.filter = "";
      this.addedOptions.push(option.toLocaleLowerCase());
      this.ceb.dispatch("update:value", [...(this.value || []), option]);
    } catch (err) {}
  }

  public remove(option: string) {
    this.addedOptions = difference(this.addedOptions, [
      option.toLocaleLowerCase(),
    ]);
    try {
      this.closeOptions();
      this.ceb.dispatch("update:value", difference(this.value, [option]));
    } catch (err) {}
  }

  public get showCreate(): boolean {
    if (!this.showAdd) {
      return false;
    }

    const option = this.filter.trim();
    if (!option) {
      return false;
    }

    if (
      this.filteredOptions.length > 0 &&
      this.containsOption(this.filteredOptions, option)
    ) {
      return false;
    }

    return (
      !this.containsOption(this.value, option) &&
      !this.containsOption(this.addedOptions, option)
    );
  }

  public get showNoOptionsText(): boolean {
    return this.options.length === 0;
  }

  public get showNoAvailableOptionsText(): boolean {
    return this.filteredOptions.length === 0;
  }

  public get showNoFilteredOptionsText(): boolean {
    return (
      !this.showAdd &&
      this.filteredOptions.length === 0 &&
      this.filter.trim().length > 0
    );
  }

  public async createOptionFromFilter() {
    const option = this.filter.trim();
    if (option.length <= 0) {
      return;
    }

    this.validation = null;
    if (this.validate !== null) {
      this.validation = await this.validate([option]);
    }

    if (this.validation === null) {
      this.addOption(option);
    }
  }

  private filterOptions(): void {
    if (this.filter.length === 0) {
      if (this.value === null || this.value.length === 0) {
        this.filteredOptions = this.options;
      } else {
        this.filteredOptions = this.options.filter(
          (o) =>
            !this.containsOption(this.value, o) &&
            !this.containsOption(this.addedOptions, o)
        );
      }
    } else {
      const lowerFilter = this.filter.toLowerCase();
      this.filteredOptions = this.options.filter((o) => {
        if (o.toLowerCase().indexOf(lowerFilter) > -1) {
          return this.value === null
            ? true
            : !this.containsOption(this.value, o) &&
                !this.containsOption(this.addedOptions, o);
        }

        return false;
      });
    }

    this.displayOptions = true;
  }

  public openOptions() {
    this.filterOptions();
  }

  public closeOptions() {
    this.displayOptions = false;
  }

  public onKeyUp() {
    this.validation = null;
    this.filterOptions();
  }

  private valueChanged() {
    this.filterOptions();
    if (this.value === null) {
      this.addedOptions.length = 0;
    }
  }

  private containsOption(options: string[], option: string): boolean {
    if (!options || !option) {
      return false;
    }

    return options
      .map((o) => o.toLocaleLowerCase())
      .includes(option.toLocaleLowerCase());
  }
}
